import type { ReactNode } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild } from '../store/selectors'

const NAV = [
  { key: 'thread', label: 'Thread', icon: ChatIcon },
  { key: 'today', label: 'Today', icon: SunIcon },
  { key: 'story', label: 'Progress', icon: ChartIcon },
  { key: 'studio', label: 'Studio', icon: SparkIcon },
  { key: 'library', label: 'Library', icon: LeafIcon },
] as const

export function AppShell({ children }: { children: ReactNode }) {
  const view = useAuroraStore((s: any) => s.ui.view)
  const child = useAuroraStore(activeChild)
  const online = useAuroraStore((s: any) => s.ui.online)
  const setView = (v: string) => (auroraStore.getState() as any).setView(v)

  return (
    <div className="shell">
      <header className="shell-top">
        <div className="shell-brand">
          <AuroraMark />
          <span className="shell-brand-name">Aurora</span>
        </div>

        {child && (
          <button
            className="shell-child"
            onClick={() => setView('child')}
            aria-current={view === 'child' ? 'page' : undefined}
          >
            <span className="shell-child-name">{child.name}</span>
          </button>
        )}

        <div className="shell-top-right">
          {!online && (
            <span className="shell-offline" title="Offline — your work is saved and will sync">
              <CloudOffIcon />
              Offline
            </span>
          )}
        </div>
      </header>

      <main className="shell-main">{children}</main>

      <nav className="shell-nav" aria-label="Main">
        {NAV.map(({ key, label, icon: Icon }) => {
          const active = view === key
          const badge = undefined as number | undefined
          return (
            <button
              key={key}
              className={`shell-nav-item${active ? ' is-active' : ''}`}
              onClick={() => setView(key)}
              aria-current={active ? 'page' : undefined}
            >
              <span className="shell-nav-icon">
                <Icon />
                {badge !== undefined && <span className="shell-nav-badge">{badge}</span>}
              </span>
              <span className="shell-nav-label">{label}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}

/* ── marks & icons ──────────────────────────────────────────
   Inline so the shell has no icon-library dependency and the stroke
   inherits currentColor cleanly in both themes. */

function AuroraMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="aurora-mark" x1="2" y1="20" x2="22" y2="4">
          <stop offset="0%" stopColor="var(--accent-teal)" />
          <stop offset="55%" stopColor="var(--accent-violet)" />
          <stop offset="100%" stopColor="var(--accent-rose)" />
        </linearGradient>
      </defs>
      <path
        d="M3 17c3.5-9 6.5-9 9-4.5S18.5 20 21 11"
        stroke="url(#aurora-mark)"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
      <path
        d="M3 21c3.5-6 6.5-6 9-3s6.5 5 9-4"
        stroke="url(#aurora-mark)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.45"
      />
    </svg>
  )
}

const S = {
  width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.75,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

function SunIcon() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg {...S}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 15l3.5-4.5 3 3L20 6" />
    </svg>
  )
}

function SparkIcon() {
  return (
    <svg {...S}>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
      <path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg {...S}>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.9L3 21l1.9-5A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
    </svg>
  )
}

function LeafIcon() {
  return (
    <svg {...S}>
      <path d="M11 20A7 7 0 0 1 4 13c0-6 7-10 16-10 0 9-4 16-9 17z" />
      <path d="M6.5 18.5C9 15 13 11.5 17 9.5" />
    </svg>
  )
}

function CloudOffIcon() {
  return (
    <svg {...S} width="14" height="14">
      <path d="M3 3l18 18" />
      <path d="M7.7 7.7A5 5 0 0 0 7 17h10a4 4 0 0 0 1.6-7.7" />
    </svg>
  )
}
